import { session } from "../auth/session";
import type { SyncRecords } from "./syncPlan";

// What cloud sync remembers between sessions, in localStorage.

// Per game: for each file, the cloud copy this browser last matched. With
// it, a file that changed only here, only in the cloud, or on both sides
// can be told apart; without it every file in both places is a conflict.
const RECORDS_PREFIX = "pocket-play.cloudSync.";

export function loadRecords(rom: string): SyncRecords {
  try {
    return (JSON.parse(localStorage.getItem(RECORDS_PREFIX + rom) ?? "{}") as SyncRecords) ?? {};
  } catch {
    return {};
  }
}

export function saveRecords(rom: string, records: SyncRecords) {
  localStorage.setItem(RECORDS_PREFIX + rom, JSON.stringify(records));
}

export function forgetRecords(rom: string) {
  localStorage.removeItem(RECORDS_PREFIX + rom);
}

/** Every game this browser has synced with the cloud. */
export function syncedRoms(): string[] {
  return Object.keys(localStorage)
    .filter((key) => key.startsWith(RECORDS_PREFIX))
    .map((key) => key.slice(RECORDS_PREFIX.length));
}

// Games, slots and saves live in browser storage that every account on this
// browser shares. They belong to the account in OWNER_KEY, which
// settleProfile (profiles.ts) sets when someone signs in, not at a first
// sync; with no owner they are signed-out play, which the next account to
// sign in adopts. Auto-sync stays off for any other account so one person's
// saves never reach another's cloud, and their cloud saves never overwrite
// the local ones.
export const OWNER_KEY = "pocket-play.localOwner";

export function signedInSub(): string | null {
  return session.getState().user?.sub || null;
}

/**
 * Whose this browser's saves are, from the signed-in account's side: "other"
 * means someone else's still (another tab kept the switch from running).
 */
export function localSavesOwnership(): "mine" | "other" | "signed-out" {
  const sub = signedInSub();
  if (!sub) return "signed-out";
  return localStorage.getItem(OWNER_KEY) === sub ? "mine" : "other";
}

/** Why cloud sync leaves this browser's saves alone, starting with `place` ("this browser", "This Mac"). */
export const NOT_YOURS = (place: string) =>
  `${place}'s saves are still another account's, because another CloudBoy tab is open or the cloud couldn't be checked. Close other CloudBoy tabs and reload to switch to this account's own.`;

/** Whether the signed-in account may move this browser's saves to or from the cloud. */
export function ownsLocalSaves(): boolean {
  return localSavesOwnership() === "mine";
}
